import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiStar, FiArrowRight, FiTrendingUp } = FiIcons;

const Results = () => {
  const stats = [
    {
      percentage: "50%",
      description: "increase in smoking cessation success with EF training vs. traditional methods alone",
      color: "from-accent-magenta to-accent-purple"
    },
    {
      percentage: "25-70%",
      description: "decrease in relapse rates when combining treatment with executive function therapy",
      color: "from-accent-gold to-accent-yellow"
    },
    {
      percentage: "85%",
      description: "report feeling \"in control\" of their choices for the first time in years",
      color: "from-green-500 to-emerald-500"
    },
    {
      percentage: "70%",
      description: "improvement in stress management without reaching for substances",
      color: "from-blue-500 to-cyan-500"
    },
    {
      percentage: "90%",
      description: "better at recognizing and avoiding triggers before they become overwhelming",
      color: "from-purple-500 to-pink-500"
    },
    {
      percentage: "2-3x",
      description: "better follow-through on recovery commitments and goals",
      color: "from-accent-lavender to-accent-light"
    }
  ];

  const testimonials = [
    {
      text: "I can actually pause now. I had years of sobriety but I was still miserable. EF therapy didn't just help me stay sober; it taught me how to live.",
      author: "M. R.",
      rating: 5
    },
    {
      text: "It's the most important thing I've done for my recovery. I'd broken so many promises about getting clean. Now I have tools to stop it when I spiral. It's not magic, it's skill.",
      author: "J. L.",
      rating: 5
    },
    {
      text: "I finally understood that my addiction wasn't just a choice. It was a brain pattern. And patterns can be changed. This gave me hope I never had before.",
      author: "D.K.",
      rating: 5
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6">
            The Recovery Numbers That{' '}
            <span className="bg-gradient-to-r from-accent-magenta to-accent-purple bg-clip-text text-transparent">
              Change Everything
            </span>
          </h2>
          <p className="text-xl text-gray-600">Our Clients in Recovery Show:</p>
        </motion.div>

        {/* Statistics Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`bg-gradient-to-br ${stat.color} rounded-3xl p-8 text-white relative overflow-hidden`}
            >
              <div className="relative z-10">
                <div className="text-3xl lg:text-4xl font-bold mb-4">{stat.percentage}</div>
                <p className="text-sm lg:text-base opacity-90 leading-relaxed">{stat.description}</p>
              </div>
              <div className="absolute top-4 right-4 opacity-20">
                <SafeIcon icon={FiTrendingUp} className="w-12 h-12" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h3 className="text-2xl lg:text-3xl font-playfair font-bold text-gray-900 mb-8 text-center">
            Let our clients speak for us:
          </h3>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="w-5 h-5 text-accent-gold fill-current" />
                  ))}
                </div>
                
                <blockquote className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </blockquote>
                
                <div className="text-accent-magenta font-semibold">
                  — {testimonial.author}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <motion.button
            onClick={() => scrollToSection('contact')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-accent-magenta text-white px-8 py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-2 hover:bg-accent-magenta/90 transition-colors duration-200"
          >
            Book Your Recovery Breakthrough Call
            <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
          </motion.button>
          
          <motion.a
            href="https://www.wethebillions.com/quiz"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="border-2 border-accent-magenta text-accent-magenta px-8 py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-2 hover:bg-accent-magenta hover:text-white transition-all duration-200"
          >
            Take the FREE Quiz
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Results;